import { CommonModule } from '@angular/common';
import JSZip from 'jszip';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild, model, ChangeDetectionStrategy } from '@angular/core';
import {
  DxDropDownBoxModule,
  DxLoadIndicatorModule,
  DxProgressBarModule,
  DxSelectBoxModule,
  DxTreeViewModule,
} from 'devextreme-angular';
import { AppConfig } from 'src/app/app.config';
import { AppGlobals } from 'src/app/app.globals';
import { DocumentCreationPayload, DocumentCreationService } from 'src/app/services/document-creation.service';
import { TemplateManagementService } from 'src/app/services/template-management.service';
import {
  FILL_TEMPLATE_PREVIEW_MESSAGE,
  FillTemplatePreviewPayload,
  FillTemplatePreviewSaveMessage,
  FillTemplatePreviewSavedMessage,
  SESSION_STORAGE_KEY_PREFIX,
} from 'src/app/pages/fill-template-preview/fill-template-preview.contract';
import { GpdButtonComponent } from '../gpd-button/gpd-button.component';
import { GenericPopupComponent, PopupButton } from '../generic-popup/generic-popup.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { ValidationToastComponent } from '../validation-toast/validation-toast.component';

export type FillTemplateContext = 'export' | 'task' | 'contract' | 'propertyOwner';

export interface FillTemplateProjectOption {
  id: number;
  name: string;
}

/** Land owner of a site, offered as a Serienbrief recipient — one filled document per owner. */
export interface FillTemplateOwnerOption {
  id: number;
  name: string;
}

/** Document type ("Dokumententyp") a generated letter is filed under on the Grundstücke page. */
export interface FillTemplateDocumentTypeOption {
  id: number;
  name: string;
}

/**
  * Template formats the fill engine accepts. Macro-enabled and template variants are included because
  * .dotx/.xltx are what Office saves templates as; they all fill into a plain .docx/.xlsx document.
  * The server decides the real format (it can read a renamed template's stored MIME type) — this list
  * only filters the file picker.
  */
const FILL_TEMPLATE_ACCEPTED_EXTENSIONS = ['.docx', '.docm', '.dotx', '.xlsx', '.xlsm', '.xltx'];

/** MIME type per format a filled document can have, as reported by the server. */
const FILL_TEMPLATE_MIME_TYPES: Record<string, string> = {
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export interface FillTemplateGeneratedDoc {
  /** Correlates a preview tab's save request (#1231) back to the doc that spawned it. */
  id: string;
  /** User-editable document name, without extension (#1240). */
  name: string;
  fileBytes: Uint8Array<ArrayBuffer>;
  /** Format of the filled document, '.docx' or '.xlsx', as reported by the server. */
  extension: string;
  /** What this document was filled for: a project id, or a property owner id in the Serienbrief run. */
  targetId: number | null;
  targetName: string | null;
  /** Serienbrief only: the type the host files the document under. */
  documentTypeId: number | null;
  added: boolean;
  adding: boolean;
  /** Set when the last filing attempt (single or "add all") failed, so the row can flag it and offer a retry. */
  addFailed?: boolean;
}

/** The generated document as a File, named and typed after the template it was filled from. */
export function fillTemplateFile(doc: FillTemplateGeneratedDoc): File {
  return new File([doc.fileBytes], `${doc.name}${doc.extension}`, {
    type: FILL_TEMPLATE_MIME_TYPES[doc.extension] ?? 'application/octet-stream',
  });
}

interface TemplateOption {
  id: number;
  name: string;
}

/** Node of the navigable template tree: folders group, documents are selectable leaves. */
interface TemplateTreeNode {
  id: string; // 'f<folderId>' | 'd<documentId>'
  parentId: string | null;
  name: string;
  icon: string;
  documentId: number | null;
}

/**
 * "Vorlage ausfüllen" flow (epic #148): template selection or own upload (#1209), project selection
 * for multi-project contracts (#1230), validation with the four result cases (#1210), automatic
 * filling (#1212) and the preview with context actions (#1231) and editable default name (#1240).
 */
@Component({
    selector: 'app-fill-template-modal',
    imports: [
        CommonModule,
        DxDropDownBoxModule,
        DxLoadIndicatorModule,
        DxProgressBarModule,
        DxSelectBoxModule,
        DxTreeViewModule,
        GenericPopupComponent,
        GpdButtonComponent,
        SvgIconComponent,
        ValidationToastComponent,
    ],
    templateUrl: './fill-template-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './fill-template-modal.component.css'
})
export class FillTemplateModalComponent implements OnDestroy {
  @ViewChild('successToast') successToast: ValidationToastComponent;
  @ViewChild('errorToast') errorToast: ValidationToastComponent;

  visible = model(false);

  @Input() context: FillTemplateContext = 'export';
  @Input() projectId: number | null = null;
  @Input() projectTaskId: number | null = null;
  @Input() contractId: number | null = null;
  /** Task context (#1207/#1578): the templates assigned to the task via task config (#173). */
  @Input() taskTemplates: { id: number; name: string }[] = [];
  /** Contract context (#1230): the projects linked to the contract. */
  @Input() linkedProjects: FillTemplateProjectOption[] = [];
  /** Serienbrief context: the site whose land owners are written to, and the owners to choose from. */
  @Input() gridConnectionId: number | null = null;
  @Input() owners: FillTemplateOwnerOption[] = [];
  /** Serienbrief context: document types the generated letters can be filed under. */
  @Input() documentTypes: FillTemplateDocumentTypeOption[] = [];
  /**
   * Context action of the preview (#1231): the host attaches the document to its task/contract
   * and resolves true on success. Not set for the export context — only Download is offered then.
   */
  @Input() addToContext: ((doc: FillTemplateGeneratedDoc) => Promise<boolean>) | null = null;

  @Output() closed = new EventEmitter<void>();

  step: 'select' | 'projects' | 'validating' | 'issues' | 'results' = 'select';

  acceptedExtensions = FILL_TEMPLATE_ACCEPTED_EXTENSIONS.join(',');

  templates: TemplateOption[] = [];
  templateTree: TemplateTreeNode[] = [];
  selectedTemplateId: number | null = null;
  selectedTemplateName: string | null = null;
  templateDropDownOpened = false;
  ownFile: File | null = null;
  isDragOver = false;

  selectedIds: number[] = [];

  /**
   * Serienbrief only: the run stays in the modal instead of opening a tab per document, so the user
   * can preview one, download them all, and file them without losing the ones that already worked.
   */
  generatedDocs: { doc: FillTemplateGeneratedDoc; pdfBytes: Uint8Array<ArrayBuffer> | null; previewLoading?: boolean }[] = [];
  selectedDocumentTypeId: number | null = null;
  addingAll = false;
  /** Live counter for the "add all" run, so the button can show "Adding… (3/20)" instead of just greying out. */
  addAllProgress: { done: number; total: number } | null = null;
  private autoCloseTimer: any = null;

  validationProgress = 0;
  private progressTimer: any = null;

  validation: {
    missingDataFields: string[];
    unsupportedFields: string[];
    noFillableFields: boolean;
    canContinue: boolean;
  } | null = null;

  /**
   * Docs generated by this component instance, keyed by id — looked up when a preview tab
   * (#1231) asks the opener to save it. Deliberately NOT cleared by reset(): a preview tab from
   * an earlier fill can still be open (and its save button still usable) after the modal has
   * been reopened for a new one.
   */
  private previewDocsById = new Map<string, FillTemplateGeneratedDoc>();

  /**
   * Blob URLs handed to a preview tab, per document. Cached because the results list lets a
   * document be previewed repeatedly, and a fresh URL each time pins another copy of the bytes.
   */
  private previewUrlsById = new Map<string, { fileUrl: string; pdfUrl: string | null }>();

  addToContextLabel =
    $localize`:@@addToTask:Add to task`;

  constructor(
    private documentCreationService: DocumentCreationService,
    private templateManagementService: TemplateManagementService,
    private cdr: ChangeDetectorRef
  ) {
    window.addEventListener('message', this.onPreviewMessage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onPreviewMessage);
    if (this.autoCloseTimer != null) clearTimeout(this.autoCloseTimer);
    this.previewUrlsById.forEach(urls => {
      URL.revokeObjectURL(urls.fileUrl);
      if (urls.pdfUrl != null) URL.revokeObjectURL(urls.pdfUrl);
    });
    this.previewUrlsById.clear();
  }

  // ---------- popup lifecycle ----------

  onShowing(): void {
    this.reset();
    this.addToContextLabel =
      this.context === 'contract' ? $localize`:@@addToContract:Add to contract`
      : this.context === 'propertyOwner' ? $localize`:@@addToDocuments:Add to documents`
      : $localize`:@@addToTask:Add to task`;
    this.loadTemplates();
  }

  onHiding(): void {
    this.stopProgress();
    if (this.autoCloseTimer != null) clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = null;
    this.closed.emit();
  }

  private reset(): void {
    this.step = 'select';
    this.templates = [];
    this.templateTree = [];
    this.selectedTemplateId = null;
    this.selectedTemplateName = null;
    this.templateDropDownOpened = false;
    this.ownFile = null;
    this.isDragOver = false;
    this.selectedIds = [];
    this.generatedDocs = [];
    // A tab may still save a document that was never filed, but anything already filed has
    // nothing left to do — without this the map keeps every letter of every run alive.
    for (const [id, doc] of Array.from(this.previewDocsById)) if (doc.added) this.previewDocsById.delete(id);
    this.selectedDocumentTypeId = this.documentTypes?.length === 1 ? this.documentTypes[0].id : null;
    this.addingAll = false;
    this.addAllProgress = null;
    if (this.autoCloseTimer != null) clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = null;
    this.validation = null;
    this.validationProgress = 0;
  }

  private async loadTemplates(): Promise<void> {
    if (this.context === 'task') {
      this.templates = (this.taskTemplates ?? []).map(t => ({ id: t.id, name: t.name }));
      this.templateTree = this.templates.map(t => ({
        id: `d${t.id}`,
        parentId: null,
        name: t.name,
        icon: 'doc',
        documentId: t.id,
      }));
      // a task usually has exactly one template configured — no point making the user open the
      // dropdown just to pick the only option that's there
      if (this.templates.length === 1) {
        this.selectedTemplateId = this.templates[0].id;
        this.selectedTemplateName = this.templates[0].name;
        this.cdr.detectChanges();
      }
      return;
    }
    try {
      const result = this.context === 'contract'
        ? await this.templateManagementService.getContractTemplateTree()
        : await this.templateManagementService.getTemplateTree();
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }

      // the contract tree is scoped to the configured folder (#690) — that root is implicit
      // context for the user and stays hidden; its children move to the top level
      let hiddenRootFolderId: number | null = null;
      if (this.context === 'contract') {
        const settings = await this.templateManagementService.getContractTemplateSettings();
        if (settings.hasError !== true) hiddenRootFolderId = settings.contractTemplatesFolderId ?? null;
      }

      const folders = (result.folderList ?? []).filter((f: any) => f.id !== hiddenRootFolderId);
      const documents = result.documents ?? [];
      const liftParent = (parentId: number | null) => (parentId === hiddenRootFolderId ? null : parentId);

      this.templates = documents.map((d: any) => ({ id: d.id, name: d.name }));
      this.templateTree = [
        ...folders.map((f: any) => ({
          id: `f${f.id}`,
          parentId: liftParent(f.parentId) != null ? `f${liftParent(f.parentId)}` : null,
          name: f.name,
          icon: 'folder',
          documentId: null,
        })),
        ...documents.map((d: any) => ({
          id: `d${d.id}`,
          parentId: liftParent(d.folderId) != null ? `f${liftParent(d.folderId)}` : null,
          name: d.name,
          icon: 'doc',
          documentId: d.id,
        })),
      ].sort((a, b) => (a.documentId == null ? 0 : 1) - (b.documentId == null ? 0 : 1) || a.name.localeCompare(b.name));
      this.cdr.detectChanges();
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  // ---------- step 1: template selection / own upload (#1209) ----------

  onTemplateTreeItemClick(e: any): void {
    const node: TemplateTreeNode = e.itemData;
    if (node.documentId == null) return; // folders only expand/collapse
    this.selectedTemplateId = node.documentId;
    this.selectedTemplateName = node.name;
    this.ownFile = null;
    this.templateDropDownOpened = false;
    // DevExtreme fires this outside Angular's change detection — without an explicit tick
    // the drop-down box keeps showing the placeholder instead of the selected template
    this.cdr.detectChanges();
  }

  clearTemplateSelection(): void {
    this.selectedTemplateId = null;
    this.selectedTemplateName = null;
  }

  /** Re-select the task's single template (#1240) after it was cleared — the fixed-display box
   *  (see showFixedTemplate) stays put and just toggles state instead of vanishing on clear,
   *  the same way the own-file drop zone keeps existing once a file is picked. */
  selectFixedTemplate(): void {
    const template = this.templates[0];
    if (template == null) return;
    this.selectedTemplateId = template.id;
    this.selectedTemplateName = template.name;
    this.ownFile = null;
  }

  /**
   * Selection only ever happens via the tree (onTemplateTreeItemClick), which sets the field's value
   * to match selectedTemplateName exactly. acceptCustomValue lets the field also accept free-typed
   * text (needed so the widget doesn't reject our own programmatic value — DevExtreme has no
   * dataSource to validate against here); if the committed value doesn't match the last real
   * selection, treat it as free text and clear rather than let a mismatched display coexist with a
   * stale selectedTemplateId that would still get submitted.
   */
  onTemplateFieldValueChanged(e: any): void {
    if (e.value == null || e.value !== this.selectedTemplateName) this.clearTemplateSelection();
  }

  onOwnFileSelected(target: any): void {
    if (target.files?.length > 0) this.setOwnFile(target.files[0]);
    target.value = '';
  }

  onDragOver(e: DragEvent): void {
    e.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onDrop(e: DragEvent): void {
    e.preventDefault();
    this.isDragOver = false;
    if (e.dataTransfer?.files?.length > 0) this.setOwnFile(e.dataTransfer.files[0]);
  }

  private setOwnFile(file: File): void {
    this.ownFile = file;
    this.clearTemplateSelection();
    this.cdr.detectChanges();
  }

  get templateChosen(): boolean {
    return this.selectedTemplateId != null || this.ownFile != null;
  }

  /**
   * A task with a single template configured has nothing to actually pick — export/contract
   * always browse a folder tree, though, where the count alone doesn't mean "nothing to choose".
   */
  get showTemplateDropdown(): boolean {
    return this.context !== 'task' || this.templates.length > 1;
  }

  get showFixedTemplate(): boolean {
    return this.context === 'task' && this.templates.length === 1;
  }

  get fixedTemplateSelected(): boolean {
    return this.selectedTemplateId != null;
  }

  // ---------- step 2: project selection for multi-project contracts (#1230) ----------

  /** The multi-select step's items: linked projects for a contract, land owners for a Serienbrief. */
  get selectionOptions(): { id: number; name: string }[] {
    return this.context === 'propertyOwner' ? (this.owners ?? []) : (this.linkedProjects ?? []);
  }

  private get needsSelection(): boolean {
    // A Serienbrief always asks: even a single owner is a deliberate choice of recipient.
    return this.context === 'propertyOwner'
      ? true
      : this.context === 'contract' && (this.linkedProjects?.length ?? 0) > 1;
  }

  continueFromSelect(): void {
    if (!this.templateChosen) return;
    if (this.needsSelection) {
      this.step = 'projects';
      return;
    }
    if (this.context === 'contract' && this.linkedProjects?.length === 1)
      this.selectedIds = [this.linkedProjects[0].id];
    this.startValidation();
  }

  toggleSelection(projectId: number): void {
    this.selectedIds = this.selectedIds.includes(projectId)
      ? this.selectedIds.filter(id => id !== projectId)
      : [...this.selectedIds, projectId];
  }

  get allSelected(): boolean {
    return this.selectionOptions.length > 0 && this.selectedIds.length === this.selectionOptions.length;
  }

  toggleAll(): void {
    this.selectedIds = this.allSelected ? [] : this.selectionOptions.map(o => o.id);
  }

  /**
   * linkedProjects is bound to a host getter that returns a fresh array of fresh objects on every
   * change detection cycle — without trackBy, *ngFor sees new object identities each tick and
   * destroys/recreates the checkbox DOM nodes, which can swap the node out from under a click
   * before it registers.
   */
  trackById(_index: number, option: { id: number }): number {
    return option.id;
  }

  // ---------- step 3: validation with progress (#1210) ----------

  async startValidation(): Promise<void> {
    this.step = 'validating';
    this.startProgress();
    try {
      const result = await this.withMinDuration(
        this.documentCreationService.validateTemplate(
          this.buildPayload(this.fillTargetIds[0] ?? null),
          this.ownFile
        )
      );
      this.stopProgress();
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        this.step = 'select';
        return;
      }
      this.validationProgress = 100;

      const hasIssues =
        result.noFillableFields === true ||
        result.missingDataFields?.length > 0 ||
        result.unsupportedFields?.length > 0;
      if (hasIssues) {
        this.validation = {
          missingDataFields: result.missingDataFields ?? [],
          unsupportedFields: result.unsupportedFields ?? [],
          noFillableFields: result.noFillableFields === true,
          // a file the engine cannot read has nothing to continue with (#1210 case 4) — the server
          // reports a null extension for exactly that case, distinct from "no tokens found"
          canContinue: result.fileExtension != null,
        };
        this.step = 'issues';
        this.cdr.detectChanges();
        return;
      }

      await this.fillTemplates();
    } catch (error) {
      this.stopProgress();
      AppGlobals.ShowErrorMessage(error);
      this.step = 'select';
    }
  }

  private startProgress(): void {
    this.validationProgress = 0;
    this.stopProgress();
    this.scheduleProgressStep();
  }

  /**
   * Simulated progress while the server validates — completes for real once the response
   * arrives. A handful of chunky, irregularly-timed leaps (rather than many small ticks on a
   * fixed interval) reads like real stepped progress pausing and catching up, instead of a
   * visibly mechanical animation.
   */
  private scheduleProgressStep(): void {
    const delay = 350 + Math.random() * 550;
    this.progressTimer = setTimeout(() => {
      if (this.validationProgress < 90) {
        const remaining = 90 - this.validationProgress;
        const jump = Math.max(4, remaining * (0.25 + Math.random() * 0.3));
        this.validationProgress = Math.min(90, this.validationProgress + jump);
        this.cdr.detectChanges();
      }
      this.scheduleProgressStep();
    }, delay);
  }

  private stopProgress(): void {
    if (this.progressTimer != null) {
      clearTimeout(this.progressTimer);
      this.progressTimer = null;
    }
  }

  /** DevExtreme's default progress bar status text ("Progress: X%") isn't localized out of the box. */
  progressStatusFormat = (ratio: number): string => {
    const percent = Math.round(ratio * 100);
    return $localize`:@@fillTemplateProgressStatus:Progress: ${percent}%`;
  };

  /**
   * Keeps the 'validating' progress screen up for at least minMs so it doesn't just flash by
   * when the server responds fast — the progress bar animation needs time to actually be seen.
   */
  private async withMinDuration<T>(work: Promise<T>, minMs = 3000): Promise<T> {
    const [result] = await Promise.all([work, new Promise(resolve => setTimeout(resolve, minMs))]);
    return result;
  }

  // ---------- step 4: fill + preview (#1212, #1231, #1240) ----------

  /** One fill per id: the selected owners, the selected contract projects, or the single host project. */
  private get fillTargetIds(): (number | null)[] {
    if (this.context === 'propertyOwner' || this.context === 'contract')
      return this.selectedIds.length > 0 ? this.selectedIds : [null];
    return [this.projectId];
  }

  async fillTemplates(): Promise<void> {
    this.step = 'validating';
    this.startProgress();
    try {
      // One shared minimum duration, not one per document: a 20-owner Serienbrief would otherwise
      // sit on the progress screen for 20 × minMs after the server was already done.
      const { docs: results, error: fillError } = await this.withMinDuration(this.fillEachTarget());
      this.stopProgress();
      if (results == null) {
        // reported only now: raised mid-loop it would sit on top of a progress bar still animating
        // out the remainder of withMinDuration's floor.
        AppGlobals.ShowErrorMessage(fillError);
        this.step = 'select';
        return;
      }

      if (this.context === 'propertyOwner') {
        // The run stays here (see generatedDocs): N preview tabs would be popup-blocked, and a
        // partial failure has to stay visible so the user can see which letters actually landed.
        this.generatedDocs = results;
        results.forEach(({ doc }) => this.previewDocsById.set(doc.id, doc));
        this.step = 'results';
        this.cdr.detectChanges();
        return;
      }

      // #1231/#1204: each generated document opens its own preview tab (Figma 5405:133176)
      // instead of an inline step here — the modal's job is done once the docs exist.
      for (const { doc, pdfBytes } of results) {
        this.previewDocsById.set(doc.id, doc);
        this.openPreviewWindow(doc, pdfBytes);
      }
      this.visible.set(false);
    } catch (error) {
      this.stopProgress();
      AppGlobals.ShowErrorMessage(error);
      this.step = 'select';
    }
  }

  /** Fills one document per target. docs is null when the server rejected one; error carries its feedback. */
  private async fillEachTarget(): Promise<{
    docs: { doc: FillTemplateGeneratedDoc; pdfBytes: Uint8Array<ArrayBuffer> | null }[] | null;
    error?: string;
  }> {
    const results: { doc: FillTemplateGeneratedDoc; pdfBytes: Uint8Array<ArrayBuffer> | null }[] = [];
    // A Serienbrief previews at most one of its letters, and rendering the PDF is the expensive half
    // of the call — previewDoc re-fills the single document the user actually opens.
    const skipPreview = this.context === 'propertyOwner';
    // one document per selected project (#1230/#1204) or per selected land owner; single targets run once
    for (const targetId of this.fillTargetIds) {
      const result = await this.documentCreationService.fillTemplate(
        { ...this.buildPayload(targetId), skipPreview },
        this.ownFile
      );
      if (result.hasError === true) {
        // ponytail: whole run fails on the first rejection. Every letter uses the same template and
        // engine, so a failure that hits one hits all — keep the partial results if that stops holding.
        return { docs: null, error: result.feedback };
      }
      results.push({
        doc: {
          id: crypto.randomUUID(),
          name: result.defaultDocumentName ?? 'document',
          fileBytes: this.base64ToArrayBuffer(result.fileBytes),
          extension: result.fileExtension,
          targetId,
          targetName: this.selectionOptions.find(o => o.id === targetId)?.name ?? null,
          // stamped now, not read at filing time: a preview tab can outlive the modal and would
          // otherwise file an old letter under whatever type a later run happens to have selected
          documentTypeId: this.selectedDocumentTypeId,
          added: false,
          adding: false,
        },
        pdfBytes: result.pdfPreviewBytes != null ? this.base64ToArrayBuffer(result.pdfPreviewBytes) : null,
      });
    }
    return { docs: results };
  }

  // ---------- step 5: Serienbrief results (preview / ZIP / file with the owner) ----------

  /** Opens one document's preview tab on demand — user-triggered, so no popup blocker. */
  async previewDoc(entry: { doc: FillTemplateGeneratedDoc; pdfBytes: Uint8Array<ArrayBuffer> | null; previewLoading?: boolean }): Promise<void> {
    if (entry.pdfBytes == null && !this.previewUrlsById.has(entry.doc.id) && entry.doc.targetId != null) {
      entry.previewLoading = true;
      try {
        const result = await this.documentCreationService.fillTemplate(
          { ...this.buildPayload(entry.doc.targetId), skipPreview: false },
          this.ownFile
        );
        if (result?.hasError !== true && result?.pdfPreviewBytes != null)
          entry.pdfBytes = this.base64ToArrayBuffer(result.pdfPreviewBytes);
      } catch (error) {
        // the preview page has a "no preview available" state; the document itself is unaffected
        console.error(error);
      } finally {
        entry.previewLoading = false;
        this.cdr.detectChanges();
      }
    }
    this.openPreviewWindow(entry.doc, entry.pdfBytes);
  }

  async downloadAllAsZip(): Promise<void> {
    try {
      await this.buildAndDownloadZip();
    } catch (error) {
      this.errorToast.showToast($localize`:@@zipDownloadFailed:The archive could not be created.`, 'Error');
      console.error(error);
    }
  }

  private async buildAndDownloadZip(): Promise<void> {
    const zip = new JSZip();
    const usedNames = new Set<string>();
    for (const { doc } of this.generatedDocs) {
      const file = fillTemplateFile(doc);
      // two owners can carry the same name, and same-named entries would collapse in the archive
      let name = file.name;
      for (let i = 2; usedNames.has(name); i++) name = `${doc.name} (${i})${doc.extension}`;
      usedNames.add(name);
      zip.file(name, file);
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.selectedTemplateName ?? 'Serienbrief'}.zip`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /** Filing needs a host callback, and on the Grundstücke page also the type to file the letter under. */
  /** The type applies to the whole run, so every letter carries the current choice. */
  onDocumentTypeChanged(): void {
    for (const { doc } of this.generatedDocs) if (!doc.added) doc.documentTypeId = this.selectedDocumentTypeId;
  }

  get canFile(): boolean {
    return this.addToContext != null && (this.context !== 'propertyOwner' || this.selectedDocumentTypeId != null);
  }

  /** A tab outliving its run files under the type stamped on the document, not the current selection. */
  private canFileDoc(doc: FillTemplateGeneratedDoc): boolean {
    return this.addToContext != null && (this.context !== 'propertyOwner' || doc.documentTypeId != null);
  }

  get canAddAll(): boolean {
    return (
      this.canFile &&
      !this.addingAll &&
      this.generatedDocs.some(({ doc }) => !doc.added)
    );
  }

  /** The results step is only reached for the property-owner Serienbrief, where a document type
   *  is required before anything can be filed. */
  get needsDocumentType(): boolean {
    return this.addToContext != null && this.selectedDocumentTypeId == null;
  }

  /** Tooltip shown on the greyed-out "Add" / "Add all" buttons so the disabled state has a
   *  visible reason instead of looking broken. Empty when the block isn't type-related. */
  get addBlockedHint(): string {
    return this.needsDocumentType
      ? $localize`:@@chooseDocumentTypeFirst:Choose a document type first`
      : '';
  }

  get addLabel(): string {
    return $localize`:@@add:Add`;
  }

  /** Rows whose last filing attempt failed and are still not filed — drives the inline error summary. */
  get failedCount(): number {
    return this.generatedDocs.filter(({ doc }) => doc.addFailed && !doc.added).length;
  }

  /** Row button label after a failed attempt — "Retry" reads clearer than a second "Add". */
  get retryLabel(): string {
    return $localize`:@@retry:Retry`;
  }

  /** Localized label for the "add all" button — swaps to a live "Adding… (3/20)" while the run is going. */
  get addAllLabel(): string {
    if (!this.addingAll) return $localize`:@@addAllToOwners:Add all`;
    const p = this.addAllProgress;
    return p
      ? $localize`:@@addingAllProgress:Adding… (${p.done}:done:/${p.total}:total:)`
      : $localize`:@@adding:Adding…`;
  }

  /** Files every document that is not filed yet, one after another, and reports the tally once. */
  async addAll(): Promise<void> {
    if (!this.canAddAll) return;
    this.addingAll = true;
    let added = 0;
    const pending = this.generatedDocs.filter(({ doc }) => !doc.added);
    this.addAllProgress = { done: 0, total: pending.length };
    for (const { doc } of pending) {
      doc.addFailed = false;
      const ok = await this.tryAddDocument(doc);
      if (ok) added++;
      else doc.addFailed = true;
      this.addAllProgress = { done: this.addAllProgress.done + 1, total: pending.length };
      this.cdr.detectChanges();
    }
    this.addingAll = false;
    this.addAllProgress = null;
    if (added === pending.length) {
      this.successToast.showToast(
        $localize`:@@documentsAddedToOwners:${added}:count: documents were added.`,
        'Success'
      );
      this.closeWhenAllFiled();
    } else {
      this.errorToast.showToast(
        $localize`:@@documentsPartiallyAdded:${added}:added: of ${pending.length}:total: documents were added.`,
        'Error'
      );
    }
    this.cdr.detectChanges();
  }

  /** Files a single document from the results list. */
  async addOne(doc: FillTemplateGeneratedDoc): Promise<void> {
    if (this.selectedDocumentTypeId == null) return;
    doc.addFailed = false;
    const success = await this.tryAddDocument(doc);
    doc.addFailed = !success;
    this.cdr.detectChanges();
    if (success) {
      this.successToast.showToast(
        $localize`:@@documentAddedToOwner:The document was added to the land owner.`,
        'Success'
      );
      this.closeWhenAllFiled();
    } else {
      this.errorToast.showToast(
        $localize`:@@documentAddFailed:The document could not be added. Please try again.`,
        'Error'
      );
    }
  }

  /** Once every generated document is filed the dialog has nothing left to do — close it after a
   *  short beat so the last row's "Added" state and the success toast stay on screen briefly. */
  private closeWhenAllFiled(): void {
    if (this.generatedDocs.length === 0 || this.generatedDocs.some(({ doc }) => !doc.added)) return;
    if (this.autoCloseTimer != null) clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = setTimeout(() => this.visible.set(false), 900);
  }

  private buildPayload(targetId: number | null): DocumentCreationPayload {
    const owner = this.context === 'propertyOwner';
    return {
      templateDocumentId: this.selectedTemplateId ?? undefined,
      // the Grundstücke page is site-scoped: the site is passed directly, the owner scopes property_*
      projectId: owner ? undefined : targetId ?? undefined,
      propertyOwnerId: owner ? targetId ?? undefined : undefined,
      gridConnectionId: this.gridConnectionId ?? undefined,
      contractId: this.contractId ?? undefined,
      projectTaskId: this.projectTaskId ?? undefined,
    };
  }

  private createBlobUrl(bytes: Uint8Array<ArrayBuffer>, type: string): string {
    return URL.createObjectURL(new Blob([bytes], { type }));
  }

  /**
   * Opens the standalone preview page (#1231) in a new tab and hands it what it needs via
   * sessionStorage — see fill-template-preview.contract.ts for why. Deliberately no
   * 'noopener': the preview tab needs window.opener to ask this component to save the doc,
   * since that action may only be possible here (e.g. a contract still unsaved in memory).
   */
  private openPreviewWindow(doc: FillTemplateGeneratedDoc, pdfBytes: Uint8Array<ArrayBuffer> | null): void {
    let urls = this.previewUrlsById.get(doc.id);
    if (urls == null) {
      urls = {
        fileUrl: this.createBlobUrl(doc.fileBytes, FILL_TEMPLATE_MIME_TYPES[doc.extension] ?? 'application/octet-stream'),
        pdfUrl: pdfBytes != null ? this.createBlobUrl(pdfBytes, 'application/pdf') : null,
      };
      this.previewUrlsById.set(doc.id, urls);
    }
    const { fileUrl, pdfUrl } = urls;
    const payload: FillTemplatePreviewPayload = {
      id: doc.id,
      name: doc.name,
      pdfUrl,
      fileUrl,
      fileExtension: doc.extension,
      primaryLabel: this.canFileDoc(doc) ? this.addToContextLabel : null,
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY_PREFIX + doc.id, JSON.stringify(payload));
    window.open(`${AppConfig.SiteBase}/fill-template-preview?key=${doc.id}`, '_blank');
  }

  /** Handles a preview tab's save request (#1231) and replies with the result. */
  private onPreviewMessage = (event: MessageEvent): void => {
    if (event.origin !== AppConfig.SiteBase) return;
    const data = event.data as FillTemplatePreviewSaveMessage | undefined;
    if (data?.type !== FILL_TEMPLATE_PREVIEW_MESSAGE.Save) return;
    const doc = this.previewDocsById.get(data.id);
    if (doc == null) return;
    doc.name = data.name ?? doc.name;
    this.addDocument(doc, event.source as Window | null);
  };

  private async addDocument(doc: FillTemplateGeneratedDoc, replyTo: Window | null): Promise<void> {
    if (doc.added || doc.adding) {
      // Filed from the results list already (or being filed right now). Reporting failure here
      // would tell the user saving broke for a document that is safely stored.
      this.replySaved(doc, replyTo, doc.added);
      return;
    }

    let success = false;
    try {
      success = await this.tryAddDocument(doc);
      if (success) {
        this.successToast.showToast(
          this.context === 'contract' ? $localize`:@@documentAddedToContract:The document was added to the contract.`
          : this.context === 'propertyOwner' ? $localize`:@@documentAddedToOwner:The document was added to the land owner.`
          : $localize`:@@documentAddedToTask:The document was added to the task.`,
          'Success'
        );
      } else {
        this.errorToast.showToast(
          $localize`:@@documentAddFailed:The document could not be added. Please try again.`,
          'Error'
        );
      }
    } finally {
      this.cdr.detectChanges();
      this.replySaved(doc, replyTo, success);
    }
  }

  private replySaved(doc: FillTemplateGeneratedDoc, replyTo: Window | null, success: boolean): void {
    const saved: FillTemplatePreviewSavedMessage = {
      type: FILL_TEMPLATE_PREVIEW_MESSAGE.Saved,
      id: doc.id,
      success,
    };
    replyTo?.postMessage(saved, AppConfig.SiteBase);
  }

  /**
   * Hands one document to the host. Toast-free so a Serienbrief can report one tally instead of
   * a toast per letter; the caller decides what to say.
   */
  private async tryAddDocument(doc: FillTemplateGeneratedDoc): Promise<boolean> {
    if (this.addToContext == null || doc.added || doc.adding) return false;
    if (this.context === 'propertyOwner' && doc.documentTypeId == null) return false;
    doc.adding = true;
    try {
      const success = (await this.addToContext(doc)) === true;
      doc.added = success;
      return success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      doc.adding = false;
    }
  }

  private base64ToArrayBuffer(base64: string): Uint8Array<ArrayBuffer> {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes;
  }

  // ---------- footer buttons ----------

  /** ZIP download lives bottom-left in the results footer: a run-wide action, kept apart from
   *  the per-document actions in the list and from "Close" on the right. */
  get leftButtons(): PopupButton[] {
    if (this.step !== 'results') return [];
    return [
      {
        text:
          this.generatedDocs.length > 1
            ? $localize`:@@downloadAllAsZip:Download all`
            : $localize`:@@downloadAsZip:Download`,
        type: 'secondary',
        icon: 'download',
        closeOnClick: false,
        onClick: () => this.downloadAllAsZip(),
      },
    ];
  }

  get buttons(): PopupButton[] {
    const cancel: PopupButton = {
      text: $localize`:@@cancel:Cancel`,
      type: 'secondary',
      onClick: () => this.visible.set(false),
    };

    switch (this.step) {
      case 'select':
        return [
          cancel,
          {
            text: $localize`:@@continue:Continue`,
            disabled: !this.templateChosen,
            closeOnClick: false,
            onClick: () => this.continueFromSelect(),
          },
        ];
      case 'projects':
        return [
          cancel,
          {
            text: $localize`:@@continue:Continue`,
            disabled: this.selectedIds.length === 0,
            closeOnClick: false,
            onClick: () => this.startValidation(),
          },
        ];
      case 'validating':
        return [cancel];
      case 'results':
        return [
          {
            text: $localize`:@@close:Close`,
            type: 'secondary',
            onClick: () => this.visible.set(false),
          },
        ];
      case 'issues':
        return this.validation?.canContinue
          ? [
              cancel,
              {
                text: $localize`:@@continue:Continue`,
                closeOnClick: false,
                onClick: () => this.fillTemplates(),
              },
            ]
          : [cancel];
    }
  }
}
